import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Default row height matches LgmDataGrid hardcoded rowHeight.
 * Used to estimate visible row count from table body height.
 */
const DEFAULT_ROW_HEIGHT = 52;

export interface LayoutMetrics {
  headerHeight: number;
  searchPanelHeight: number;
  tableBodyHeight: number;
  /** Floor(tableBodyHeight / DEFAULT_ROW_HEIGHT) */
  visibleRowEstimate: number;
  timestamp: number;
}

type LayoutRole = 'app-header' | 'search-panel' | 'table-body';

const LAYOUT_ROLES: LayoutRole[] = ['app-header', 'search-panel', 'table-body'];

const EMPTY_METRICS: LayoutMetrics = {
  headerHeight: 0,
  searchPanelHeight: 0,
  tableBodyHeight: 0,
  visibleRowEstimate: 0,
  timestamp: 0,
};

declare global {
  interface Window {
    __lgm_getLayoutMetrics?: () => LayoutMetrics;
  }
}

function queryHeight(role: LayoutRole): number {
  const el = document.querySelector(`[data-layout-role="${role}"]`);
  return el ? el.getBoundingClientRect().height : 0;
}

/** Reads current DOM heights synchronously — safe to call from Playwright via window. */
function collectMetrics(): LayoutMetrics {
  const headerHeight = queryHeight('app-header');
  const searchPanelHeight = queryHeight('search-panel');
  const tableBodyHeight = queryHeight('table-body');

  return {
    headerHeight,
    searchPanelHeight,
    tableBodyHeight,
    visibleRowEstimate: Math.floor(tableBodyHeight / DEFAULT_ROW_HEIGHT),
    timestamp: Date.now(),
  };
}

/**
 * Tracks computed pixel heights of key layout regions using ResizeObserver.
 *
 * Targets elements marked with `data-layout-role` attributes:
 *   - `"app-header"`   — sticky nav bar in LgmProductNavigator
 *   - `"search-panel"` — TableSearch container on each page
 *   - `"table-body"`   — data grid container on each page
 *
 * Also exposes `window.__lgm_getLayoutMetrics()` for Playwright assertions
 * (non-production only).
 *
 * Mount this hook inside `DebugLayout` which is already gated to dev/stage.
 */
export function useLayoutMetrics(): LayoutMetrics {
  const [metrics, setMetrics] = useState<LayoutMetrics>(EMPTY_METRICS);
  const rafRef = useRef<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      setMetrics(collectMetrics());
      rafRef.current = null;
    });
  }, []);

  // Re-attach ResizeObserver whenever the observed elements may have changed.
  // A MutationObserver on <body> detects when data-layout-role nodes appear/
  // disappear (e.g. search-panel toggled by A/B flag or page navigation).
  useEffect(() => {
    function attachObserver(): void {
      observerRef.current?.disconnect();

      const ro = new ResizeObserver(scheduleUpdate);
      LAYOUT_ROLES.forEach(role => {
        const el = document.querySelector(`[data-layout-role="${role}"]`);
        if (el) {
          ro.observe(el);
        }
      });
      observerRef.current = ro;
      scheduleUpdate();
    }

    attachObserver();

    // Watch for structural DOM changes so that dynamically mounted/unmounted
    // data-layout-role nodes (e.g. the search panel) are picked up.
    const mo = new MutationObserver(mutations => {
      const relevant = mutations.some(m => {
        const checkNode = (node: Node): boolean =>
          node instanceof Element &&
          (node.hasAttribute('data-layout-role') ||
            node.querySelector('[data-layout-role]') !== null);
        return (
          Array.from(m.addedNodes).some(checkNode) ||
          Array.from(m.removedNodes).some(checkNode)
        );
      });
      if (relevant) {
        attachObserver();
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // Expose synchronous snapshot for Playwright (non-production only).
    if (process.env.NODE_ENV !== 'production') {
      window.__lgm_getLayoutMetrics = collectMetrics;
    }

    return () => {
      observerRef.current?.disconnect();
      mo.disconnect();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      delete window.__lgm_getLayoutMetrics;
    };
  }, [scheduleUpdate]);

  return metrics;
}
